(function () {
  const readyQueue = [];
  let waitingForFilters = false;
  let filtersTimedOut = false;

  function getFiltersApi() {
    return window.collectionMainFilters || null;
  }

  function hasWorkingFiltersApi() {
    const api = getFiltersApi();
    return Boolean(
      api
      && (typeof api.applyShopByModelSelection === 'function' || typeof api.applyFilterSelection === 'function')
    );
  }

  function disableFilterTiles() {
    document.querySelectorAll('[data-collection-shop-by-model] [data-shop-by-model-item]').forEach((item) => {
      item.disabled = true;
      item.setAttribute('aria-disabled', 'true');
      item.classList.add('opacity-50', 'cursor-not-allowed');
      // Fail open: the filter API never showed up at all, so we can't confirm product
      // counts — reveal tiles that were left pending rather than hide them forever.
      if (item.getAttribute('data-count-pending') === 'true') {
        item.hidden = false;
        item.removeAttribute('aria-hidden');
        item.removeAttribute('data-count-pending');
      }
    });
  }

  function flushReadyQueue() {
    if (!hasWorkingFiltersApi()) return;
    waitingForFilters = false;
    while (readyQueue.length) {
      const run = readyQueue.shift();
      try {
        run(getFiltersApi());
      } catch (error) {
        console.error(error);
      }
    }
  }

  function rejectReadyQueue() {
    filtersTimedOut = true;
    waitingForFilters = false;
    readyQueue.length = 0;
    disableFilterTiles();
    console.warn('[collection-shop-by-model] Filter API unavailable; model tile filters were not applied.');
  }

  function whenFiltersReady(run) {
    if (hasWorkingFiltersApi()) {
      run(getFiltersApi());
      return;
    }
    if (filtersTimedOut) return;

    readyQueue.push(run);
    if (waitingForFilters) return;
    waitingForFilters = true;

    document.addEventListener('collection-main:ready', flushReadyQueue, { once: true });

    let attempts = 0;
    const poll = setInterval(() => {
      if (hasWorkingFiltersApi()) {
        clearInterval(poll);
        flushReadyQueue();
        return;
      }
      attempts += 1;
      if (attempts >= 120) {
        clearInterval(poll);
        rejectReadyQueue();
      }
    }, 50);
  }

  // Each tile carries one or more independent filter groups (brand, condition,
  // make/model, processor series, "other", advanced raw fallback). Different
  // groups AND together — a product must match every group a tile has. Values
  // within the SAME group OR together (pipe-joined), same as before.
  function readFilterGroups(item) {
    const raw = item.getAttribute('data-filter-groups');
    if (!raw) return [];
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      console.error('[collection-shop-by-model] Invalid data-filter-groups JSON', error);
      return [];
    }
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((group) => {
        const field = String(group && group.field || '').trim();
        const specGroup = String(group && group.specGroup || '').trim();
        const values = String(group && group.values || '')
          .split('|')
          .map((value) => value.trim())
          .filter(Boolean);
        if (!field || !values.length) return null;
        return { field, specGroup, values };
      })
      .filter(Boolean);
  }

  function resolveGroupValues(group, api) {
    return group.values.map((rawValue) => {
      if (api && typeof api.resolveFilterPair === 'function') {
        return api.resolveFilterPair(group.field, rawValue, group.specGroup) || rawValue;
      }
      if (group.field === 'spec_pairs' && group.specGroup && !rawValue.includes('=')) {
        return `${group.specGroup}=${rawValue}`;
      }
      return rawValue;
    }).filter(Boolean);
  }

  function dispatchApplyFilterEvent(detail) {
    document.dispatchEvent(
      new CustomEvent('collection:apply-filter', {
        bubbles: true,
        detail
      })
    );
  }

  function applyModelFilter(item) {
    const groups = readFilterGroups(item);
    if (!groups.length) return;

    whenFiltersReady((api) => {
      const resolvedGroups = groups.map((group) => {
        const resolvedValues = resolveGroupValues(group, api);
        const values = resolvedValues.length ? resolvedValues : group.values;
        return { field: group.field, specGroup: group.specGroup, values };
      }).filter((group) => group.values.length);

      if (!resolvedGroups.length) return;

      // applyShopByModelGroups applies every group under a single clearAll(), so
      // AND-combined groups (brand + condition + spec_pairs, etc.) all survive
      // together — applyShopByModelSelection/applyFilterSelection each call
      // clearAll() internally, so calling either more than once per click would
      // wipe out everything applied before it.
      if (api && typeof api.applyShopByModelGroups === 'function') {
        api.applyShopByModelGroups(resolvedGroups);
      } else {
        resolvedGroups.forEach((group) => {
          if (group.field === 'spec_pairs' && api && typeof api.applyShopByModelSelection === 'function') {
            api.applyShopByModelSelection(group.specGroup, group.values, { toggleOff: true });
          } else if (api && typeof api.applyFilterSelection === 'function') {
            group.values.forEach((value, index) => {
              api.applyFilterSelection(group.field, value, { replaceSection: index === 0 });
            });
          } else {
            dispatchApplyFilterEvent({
              field: group.field,
              values: group.values,
              specGroup: group.specGroup,
              mode: 'shop-by-model',
              source: 'shop-by-model'
            });
          }
        });
      }

      if (api && typeof api.scrollToProducts === 'function') {
        api.scrollToProducts();
      }
    });
  }

  function getTileCount(item, api) {
    const groups = readFilterGroups(item);
    if (!groups.length) return null;

    // AND across groups: a tile only has matching products if EVERY group has
    // at least one. The minimum of the per-group OR-sums is >0 iff all are >0.
    const groupCounts = groups.map((group) => {
      const resolvedValues = resolveGroupValues(group, api);
      const valuesToCheck = resolvedValues.length ? resolvedValues : group.values;
      return valuesToCheck.reduce(
        (sum, value) => sum + Number(api.getFacetValueCount(group.field, value) || 0),
        0
      );
    });

    return Math.min(...groupCounts);
  }

  // Same "only show what has matching product count" check the filter sidebar uses —
  // and the same skeleton-until-ready mechanism: tiles with a filter start out
  // `hidden` (set directly in the Liquid markup), same as the sidebar's spec-group
  // skeletons stand in before real filter data is in. Nothing is ever shown and then
  // yanked away; a tile only gets revealed once we've confirmed it actually has at
  // least one matching product. Tiles with no filter configured at all are untouched
  // — that's a different, pre-existing state, never part of this check.
  //
  // `collection-main:ready` only means the filter API object exists, not that the
  // async facet-count fetch behind it has actually finished — so a single read right
  // at that moment isn't trustworthy yet. Poll (same idiom as whenFiltersReady above)
  // until the facet data itself has actually arrived (api.hasFacetData()), not until
  // some tile's count happens to be positive — a filter can genuinely have zero
  // matches, and that's not the same thing as "data hasn't loaded yet".
  function revealMatchingTiles(root, api) {
    if (!api || typeof api.getFacetValueCount !== 'function') return;

    const items = [...root.querySelectorAll('[data-shop-by-model-item][data-count-pending="true"]')];
    if (!items.length) return;

    function reveal(item) {
      item.hidden = false;
      item.removeAttribute('aria-hidden');
      item.removeAttribute('data-count-pending');
    }

    let attempts = 0;
    const poll = setInterval(() => {
      const dataLoaded = typeof api.hasFacetData === 'function' && api.hasFacetData();
      attempts += 1;

      if (!dataLoaded && attempts < 30) return; // keep waiting, up to ~4.5s

      clearInterval(poll);
      if (!dataLoaded) {
        // Data never loaded — fail open (reveal everything) rather than leave every
        // tile permanently hidden.
        console.warn('[collection-shop-by-model] Facet counts never loaded; revealing all tiles.');
        items.forEach(reveal);
        return;
      }

      items.forEach((item) => {
        const count = getTileCount(item, api);
        if (count === null || count > 0) reveal(item);
      });
    }, 150);
  }

  function initShopByModel(root) {
    if (!root || root.dataset.shopByModelInit === 'true') return;
    root.dataset.shopByModelInit = 'true';

    root.querySelectorAll('[data-shop-by-model-item]').forEach((item) => {
      if (item.disabled || item.getAttribute('aria-disabled') === 'true') return;
      item.addEventListener('click', (event) => {
        event.preventDefault();
        applyModelFilter(item);
      });
    });

    whenFiltersReady((api) => revealMatchingTiles(root, api));
  }

  function initAll() {
    document.querySelectorAll('[data-collection-shop-by-model]').forEach(initShopByModel);
  }

  window.initCollectionShopByModelSection = initShopByModel;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('collection-main:ready', flushReadyQueue);
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-collection-shop-by-model]');
    if (section) initShopByModel(section);
  });
})();
